import { makeStyles, Menu, MenuButton, MenuItem, MenuList, MenuPopover, MenuTrigger, mergeClasses } from '@fluentui/react-components';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IFilterItem } from '../../../data-types/filterItem';
import { getFilteredFilterItems, getFilterItemDisplayLabel } from '../../../utils/FilterUtils';
import { useFilterPickerStyles } from '../../filters/filter-picker/FilterPicker';
import FilterSearchBar from '../../filters/filter-search-bar/FilterSearchBar';

export interface IScrollToSectionDropdown {
    sections: IFilterItem[];
    label: string;
    isDisabled: boolean;
    onSectionChange: (filteredItems: IFilterItem[]) => void;
}

const useStyles = makeStyles({
    // Fluent MenuPopover defaults to max-width: 300px; MenuItem to 290px — override to match FilterPicker.
    surface: {
        maxWidth: '540px',
        width: '540px',
        minWidth: '540px',
    },
    menuList: {
        width: '100%',
    },
    menuItem: {
        maxWidth: '100%',
        width: '100%',
    },
});

const ScrollToSectionDropdown: React.FC<IScrollToSectionDropdown> = ({ sections, label, isDisabled, onSectionChange }) => {
    const { t } = useTranslation();
    const styles = useStyles();
    const pickerStyles = useFilterPickerStyles();
    const [open, setOpen] = useState(false);
    const [searchValue, setSearchValue] = useState('');

    const visibleItems = useMemo(() => getFilteredFilterItems(sections, searchValue), [sections, searchValue]);
    const hasResults = visibleItems.length > 0;

    const announcementMessage = useMemo(() => {
        const filteredCount = visibleItems.length;
        const unfilteredCount = sections.length;
        if (filteredCount === unfilteredCount) {
            return '';
        }
        if (!hasResults) {
            return t('noSectionsFound').toString();
        }
        return t('sections_found', { count: filteredCount }).toString();
    }, [visibleItems.length, sections.length, hasResults, t]);

    const resetOnClose = () => {
        setSearchValue('');
    };

    const triggerLabel = t(label);
    const menuAriaLabel = t('goToSection').toString();

    return (
        <Menu
            open={open}
            onOpenChange={(_ev, data) => {
                setOpen(data.open);
                if (!data.open) {
                    resetOnClose();
                }
            }}
            positioning={{ position: 'below', align: 'start', offset: { mainAxis: 10 } }}
        >
            <MenuTrigger disableButtonEnhancement>
                <MenuButton
                    aria-label={triggerLabel}
                    disabled={isDisabled}
                    disabledFocusable={isDisabled}
                    appearance='secondary'
                    className={mergeClasses(open && pickerStyles.openTrigger)}
                >
                    {triggerLabel}
                </MenuButton>
            </MenuTrigger>
            <MenuPopover className={mergeClasses(pickerStyles.surface, styles.surface)}>
                <div
                    id='filterOptionsContainer'
                    data-testid='filter-options-container'
                    className={pickerStyles.contentContainer}
                    tabIndex={0}
                    aria-label={menuAriaLabel}
                >
                    <div className={pickerStyles.menuHeaderRow}>
                        {announcementMessage ? (
                            <div className={pickerStyles.announced} aria-live='polite' aria-atomic='true'>
                                {announcementMessage}
                            </div>
                        ) : null}
                        <div tabIndex={0}>
                            <FilterSearchBar
                                searchBoxPlaceholderText={t('search')}
                                onSearchBoxChange={(_ev, newValue) => setSearchValue(newValue ?? '')}
                                onAbort={() => setSearchValue('')}
                            />
                        </div>
                    </div>
                    <div className={pickerStyles.filterItemsScrollable} aria-label='Filter Items Container'>
                        {!hasResults ? (
                            <div className={pickerStyles.noResults}>{t('noSectionsFound')}</div>
                        ) : (
                            <MenuList className={mergeClasses(pickerStyles.list, styles.menuList)} aria-label={menuAriaLabel}>
                                {visibleItems.map((item) => (
                                    <MenuItem
                                        key={item.key}
                                        className={mergeClasses(pickerStyles.listItem, styles.menuItem)}
                                        onClick={() => onSectionChange([item])}
                                    >
                                        {getFilterItemDisplayLabel(item)}
                                    </MenuItem>
                                ))}
                            </MenuList>
                        )}
                    </div>
                </div>
            </MenuPopover>
        </Menu>
    );
};

export default ScrollToSectionDropdown;
